import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import { circlePolygon } from "./geo_circle"

// Handles property maps on index (clustered), show (single pin), and area
// listings (a fuzzy center+radius circle that never exposes the exact parcel).
export default class extends Controller {
  static targets = ["container"]
  static values = {
    properties: Array,   // [{id, lat, lng, title, price, url}]  for index map
    latitude: Number,    // for show / circle map
    longitude: Number,   // for show / circle map
    radiusMiles: Number, // for circle map (area listings)
    mode: String,        // "cluster" | "pin" | "circle"
  }

  connect() {
    const token = document.querySelector('meta[name="mapbox-token"]')?.content
    if (!token) {
      console.warn("Mapbox token not found. Set MAPBOX_ACCESS_TOKEN env var.")
      return
    }
    mapboxgl.accessToken = token

    const target = this.hasContainerTarget ? this.containerTarget : this.element

    if (this.modeValue === "pin") {
      this.#initPinMap(target)
    } else if (this.modeValue === "circle") {
      this.#initCircleMap(target)
    } else {
      this.#initClusterMap(target)
    }
  }

  disconnect() {
    this.map?.remove()
  }

  // ── Single-pin map (show page) ────────────────────────────────────────────

  #initPinMap(container) {
    const lat = this.latitudeValue
    const lng = this.longitudeValue
    if (!lat || !lng) return

    this.map = new mapboxgl.Map({
      container,
      style: "mapbox://styles/mapbox/dark-v11",
      center: [lng, lat],
      zoom: 11,
    })

    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")

    new mapboxgl.Marker({ color: "#2F4F4F" })
      .setLngLat([lng, lat])
      .addTo(this.map)
  }

  // ── Fuzzy area circle (area listings — center + radius, no exact pin) ──────

  #initCircleMap(container) {
    const lat = this.latitudeValue
    const lng = this.longitudeValue
    const radius = this.radiusMilesValue
    if (!lat || !lng || !radius || radius <= 0) return

    this.map = new mapboxgl.Map({
      container,
      style: "mapbox://styles/mapbox/dark-v11",
      center: [lng, lat],
      zoom: 8,
    })
    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")
    this.map.scrollZoom.disable() // a marketing map, not a navigation tool

    this.map.on("load", () => {
      this.map.addSource("area", { type: "geojson", data: circlePolygon(lng, lat, radius) })
      this.map.addLayer({
        id: "area-fill", type: "fill", source: "area",
        paint: { "fill-color": "#48BA16", "fill-opacity": 0.15 },
      })
      this.map.addLayer({
        id: "area-line", type: "line", source: "area",
        paint: { "line-color": "#48BA16", "line-width": 2 },
      })

      const dLat = radius / 69
      const dLng = radius / (69 * Math.cos((lat * Math.PI) / 180))
      this.map.fitBounds([[lng - dLng, lat - dLat], [lng + dLng, lat + dLat]], { padding: 30, duration: 0 })
    })
  }

  // ── Clustered markers map (index page) ───────────────────────────────────

  #initClusterMap(container) {
    const properties = this.propertiesValue
    if (!properties.length) return

    // Default center to centroid of all properties
    const lats = properties.map(p => p.lat)
    const lngs = properties.map(p => p.lng)
    const center = [
      lngs.reduce((a, b) => a + b, 0) / lngs.length,
      lats.reduce((a, b) => a + b, 0) / lats.length,
    ]

    this.map = new mapboxgl.Map({
      container,
      style: "mapbox://styles/mapbox/dark-v11",
      center,
      zoom: 6,
    })

    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")

    this.map.on("load", () => {
      const geojson = {
        type: "FeatureCollection",
        features: properties.map(p => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [p.lng, p.lat] },
          properties: { id: p.id, title: p.title, price: p.price, url: p.url },
        })),
      }

      this.map.addSource("properties", {
        type: "geojson",
        data: geojson,
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 50,
      })

      // Cluster circles
      this.map.addLayer({
        id: "clusters",
        type: "circle",
        source: "properties",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": [
            "step", ["get", "point_count"],
            "#51bbd6", 5, "#f1a206", 20, "#d4380d",
          ],
          "circle-radius": [
            "step", ["get", "point_count"],
            18, 5, 24, 20, 30,
          ],
        },
      })

      // Cluster count labels
      this.map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "properties",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-size": 12,
        },
        paint: { "text-color": "#fff" },
      })

      // Individual property pins
      this.map.addLayer({
        id: "unclustered-point",
        type: "circle",
        source: "properties",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": "#48BA16",
          "circle-radius": 10,
          "circle-stroke-width": 3,
          "circle-stroke-color": "#fff",
        },
      })

      // Zoom into cluster on click
      this.map.on("click", "clusters", e => {
        const features = this.map.queryRenderedFeatures(e.point, { layers: ["clusters"] })
        const clusterId = features[0].properties.cluster_id
        this.map.getSource("properties").getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return
          this.map.easeTo({ center: features[0].geometry.coordinates, zoom })
        })
      })

      // Popup on individual pin click
      this.map.on("click", "unclustered-point", e => {
        const { title, price, url } = e.features[0].properties
        const coords = e.features[0].geometry.coordinates.slice()

        new mapboxgl.Popup({ className: "wl-map-popup" })
          .setLngLat(coords)
          .setHTML(`
            <div style="min-width:200px; padding-top:4px">
              <strong>${title}</strong><br>
              <span style="color:#a0a0a0">$${price}/day</span>
              <a href="${url}" class="btn btn-sm btn-primary mt-2 d-block w-100 text-center">View Details</a>
            </div>
          `)
          .addTo(this.map)
      })

      this.map.on("mouseenter", "clusters", () => { this.map.getCanvas().style.cursor = "pointer" })
      this.map.on("mouseleave", "clusters", () => { this.map.getCanvas().style.cursor = "" })
      this.map.on("mouseenter", "unclustered-point", () => { this.map.getCanvas().style.cursor = "pointer" })
      this.map.on("mouseleave", "unclustered-point", () => { this.map.getCanvas().style.cursor = "" })
    })
  }
}
